import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, NavigationError, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '@core/auth/auth.service';
import { ClientDiagnosticsService } from '@core/diagnostics/client-diagnostics.service';
import { VisitLogService } from '@core/diagnostics/visit-log.service';
import { LocalProfileService } from '@shared/services/local-profile.service';
import { AccountModalsComponent } from '@shared/components/account-modals/account-modals.component';
import { DevCaptureButtonComponent } from '@shared/components/dev-capture-button/dev-capture-button.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, AccountModalsComponent, DevCaptureButtonComponent],
  template: `
    <div class="shell">
      <header class="app-header">
        <div class="header-bar">
          <div class="brand">
            <a routerLink="/" class="brand-link" (click)="closeMenu()" aria-label="Commpare">
              <img src="assets/commpare-logo.png" alt="Commpare" class="brand-logo" />
              <span class="visually-hidden">Commpare</span>
            </a>
          </div>

          <div class="header-mobile-actions">
            <!-- Mobile Account Button -->
            <button
              type="button"
              class="header-action-btn header-account-btn"
              data-testid="mobile-account-button"
              (click)="onMobileAccountClick()"
              [attr.aria-label]="auth.isLoggedIn ? 'תפריט חשבון: ' + userDisplayName : 'התחברות עם Google'"
            >
              @if (auth.isLoggedIn) {
                @if (auth.currentUser?.pictureUrl) {
                  <img [src]="auth.currentUser?.pictureUrl" alt="" class="header-avatar-img" />
                } @else {
                  <span class="header-avatar-fallback">{{ (userDisplayName || 'U')[0] }}</span>
                }
              } @else {
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              }
            </button>

            <!-- Location Settings Button -->
            <button
              type="button"
              class="header-location-btn"
              aria-label="הגדרות עיר וסניף להשוואה"
              title="בחירת עיר וסניף להשוואת מחירים"
              data-testid="header-location-button"
              (click)="openLocationSettings()"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 12 8 12s8-6.75 8-12a8 8 0 0 0-8-8z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </button>

            <!-- Mobile Hamburger Button -->
            <button
              type="button"
              class="hamburger-btn"
              [class.is-active]="isMenuOpen"
              [attr.aria-expanded]="isMenuOpen"
              aria-controls="top-nav"
              [attr.aria-label]="isMenuOpen ? 'סגור תפריט' : 'פתח תפריט'"
              data-testid="nav-menu-toggle"
              (click)="toggleMenu()"
            >
              <svg
                class="hamburger-icon"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                @if (!isMenuOpen) {
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                } @else {
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                }
              </svg>
            </button>
          </div>
        </div>

        <nav
          id="top-nav"
          class="top-nav"
          [class.open]="isMenuOpen"
          dir="rtl"
          aria-label="תפריט ראשי"
          (click)="onNavClick($event)"
        >
          @if (auth.isAdmin) {
            <a
              routerLink="/shop"
              routerLinkActive="active"
              data-testid="nav-shopping-home"
            >
              רשימת קניות חכמה
            </a>
          }
          <a
            routerLink="/baskets"
            routerLinkActive="active"
            data-testid="nav-baskets-manager"
          >
            הרשימות שלי
          </a>
          @if (auth.isAdmin) {
            <div class="admin-nav-item">
              <button
                type="button"
                class="admin-menu-trigger"
                [class.active]="isAdminMenuActive"
                [attr.aria-expanded]="isAdminMenuOpen"
                (click)="toggleAdminMenu($event)"
              >
                <span>ניהול</span>
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              @if (isAdminMenuOpen) {
                <div class="admin-dropdown-menu" role="menu">
                  <div class="admin-menu-section-title">ניתוח והשוואה</div>
                  <a routerLink="/legacy-compare" routerLinkActive="active" data-testid="nav-price-compare" (click)="closeAdminMenu()">
                    השוואה לפי מוצרים
                  </a>
                  <a routerLink="/similar" routerLinkActive="active" data-testid="nav-similar-products" (click)="closeAdminMenu()">
                    מוצרים דומים
                  </a>
                  <a routerLink="/price-history" routerLinkActive="active" data-testid="nav-price-history" (click)="closeAdminMenu()">
                    היסטוריית מחירים
                  </a>

                  <div class="menu-divider"></div>
                  <div class="admin-menu-section-title">סיווג ותיוג</div>
                  <a routerLink="/admin/classification" routerLinkActive="active" data-testid="nav-classification" (click)="closeAdminMenu()">
                    סיווג מוצרים
                  </a>
                  <a routerLink="/admin/classification-flow" routerLinkActive="active" data-testid="nav-classification-flow" (click)="closeAdminMenu()">
                    Classification flow
                  </a>
                  <a routerLink="/admin/classify-probe" routerLinkActive="active" data-testid="nav-classify-probe" (click)="closeAdminMenu()">
                    Classify probe
                  </a>

                  <div class="menu-divider"></div>
                  <div class="admin-menu-section-title">תפעול ובדיקות</div>
                  <a routerLink="/admin/name-preview" routerLinkActive="active" data-testid="nav-name-preview" (click)="closeAdminMenu()">
                    תצוגת שם
                  </a>
                  <a routerLink="/admin/unit-reviews" routerLinkActive="active" data-testid="nav-unit-reviews" (click)="closeAdminMenu()">
                    בדיקת יחידות
                  </a>
                  <a routerLink="/admin/workers" routerLinkActive="active" data-testid="nav-workers" (click)="closeAdminMenu()">
                    Workers Dashboard
                  </a>
                  <a routerLink="/admin/visits" routerLinkActive="active" data-testid="nav-visit-log" (click)="closeAdminMenu()">
                    יומן ביקורים
                  </a>
                </div>
              }
            </div>
          }
        </nav>

        <!-- Desktop Account Button & Dropdown -->
        <div class="header-desktop-account" dir="rtl">
          @if (!auth.isLoggedIn) {
            <button
              type="button"
              class="desktop-login-btn"
              data-testid="desktop-google-login-button"
              (click)="openModal('login')"
            >
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"/>
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"/>
                <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.17 0 9.99 0 12s.45 3.83 1.25 5.42l4.03-3.15z"/>
                <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
              </svg>
              <span>התחברות</span>
            </button>
          } @else {
            <button
              type="button"
              class="desktop-user-pill"
              data-testid="desktop-user-menu-button"
              (click)="toggleAccountMenu()"
              [attr.aria-expanded]="isAccountMenuOpen"
            >
              @if (auth.currentUser?.pictureUrl) {
                <img [src]="auth.currentUser?.pictureUrl" alt="" class="pill-avatar-img" />
              } @else {
                <span class="pill-avatar-fallback">{{ (userDisplayName || 'U')[0] }}</span>
              }
              <span class="pill-user-name">{{ userDisplayName }}</span>
              <svg class="pill-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          }
        </div>

        <!-- Dropdown Menu (Shared for desktop and mobile) -->
        @if (isAccountMenuOpen) {
          <div class="account-dropdown-menu" role="menu" data-testid="account-dropdown-menu" dir="rtl">
            @if (auth.isLoggedIn) {
              <div class="menu-user-header">
                <strong>{{ auth.currentUser?.givenName }} {{ auth.currentUser?.familyName }}</strong>
                <span class="menu-email" dir="ltr">{{ auth.currentUser?.email }}</span>
              </div>
              <div class="menu-divider"></div>
              <button type="button" class="menu-item" (click)="openModal('profile'); closeAccountMenu()">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>הפרטים שלי</span>
              </button>
              <button type="button" class="menu-item" (click)="openModal('groups'); closeAccountMenu()">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span>הקבוצות שלי</span>
              </button>
              <a routerLink="/baskets" class="menu-item" (click)="closeAccountMenu()">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
                <span>הרשימות שלי</span>
              </a>
              <div class="menu-divider"></div>
              <button type="button" class="menu-item menu-item--danger" (click)="logout(); closeAccountMenu()">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                  <polyline points="8 17 3 12 8 7"></polyline>
                  <line x1="3" y1="12" x2="15" y2="12"></line>
                </svg>
                <span>התנתקות</span>
              </button>
            } @else {
              <a routerLink="/baskets" class="menu-item" data-testid="account-menu-store-list" (click)="closeAccountMenu()">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
                <span>הרשימות שלי</span>
              </a>
              <button type="button" class="menu-item" (click)="openModal('login'); closeAccountMenu()">
                <span>התחברות עם Google</span>
              </button>
            }
          </div>
        }
      </header>

      <main class="app-main">
        <router-outlet />
      </main>

      <app-account-modals
        [modalType]="activeModal"
        (modalTypeChange)="onModalTypeChange($event)"
      ></app-account-modals>

      <app-dev-capture-button />
    </div>
  `,
  styles: [
    `
      .shell {
        min-height: 100vh;
        box-sizing: border-box;
        padding-top: var(--app-header-height, 4.25rem);
      }

      .app-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1.5rem;
        padding: 0.8rem 1.5rem;
        border-bottom: 1px solid var(--border-subtle, #e2ece5);
        background: rgba(255, 255, 255, 0.96);
        backdrop-filter: blur(14px);
        -webkit-backdrop-filter: blur(14px);
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 100;
        direction: ltr;
        box-shadow: 0 1px 6px rgba(14, 30, 22, 0.03);
      }

      .header-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
        direction: ltr;
      }

      .brand-link {
        display: inline-flex;
        align-items: center;
        text-decoration: none;
        line-height: 1;
      }

      .brand-logo {
        height: 38px;
        width: auto;
        max-width: 175px;
        object-fit: contain;
        display: block;
        transition: opacity 0.15s ease, transform 0.15s ease;
      }

      .brand-link:hover .brand-logo {
        opacity: 0.88;
        transform: scale(1.02);
      }

      .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .header-mobile-actions {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        direction: ltr;
      }

      .header-action-btn,
      .header-location-btn,
      .hamburger-btn {
        display: none;
      }

      .header-desktop-account {
        position: relative;
        display: flex;
        align-items: center;
      }

      .desktop-login-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        height: 38px;
        padding: 0 1.15rem;
        border: 1px solid var(--border-subtle, #e2ece5);
        border-radius: 9999px;
        background: var(--bg-surface, #ffffff);
        color: var(--text-main, #0e1e16);
        font-weight: 600;
        font-size: 0.9rem;
        cursor: pointer;
        box-shadow: 0 1px 3px rgba(14, 30, 22, 0.04);
        transition: all 0.15s ease;
      }
      .desktop-login-btn:hover {
        background: var(--emerald-50, #ecfdf5);
        border-color: var(--emerald-200, #a7f3d0);
        color: var(--emerald-800, #065f46);
        transform: translateY(-1px);
      }

      .desktop-user-pill {
        display: inline-flex;
        align-items: center;
        gap: 0.55rem;
        height: 38px;
        padding: 0 0.85rem 0 0.45rem;
        border: 1px solid var(--border-subtle, #e2ece5);
        border-radius: 9999px;
        background: var(--bg-surface, #ffffff);
        cursor: pointer;
        box-shadow: 0 1px 3px rgba(14, 30, 22, 0.04);
        transition: all 0.15s ease;
      }
      .desktop-user-pill:hover {
        background: var(--emerald-50, #ecfdf5);
        border-color: var(--emerald-200, #a7f3d0);
      }
      .pill-avatar-img {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        object-fit: cover;
      }
      .pill-avatar-fallback {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: var(--emerald-600, #059669);
        color: #ffffff;
        font-weight: 700;
        font-size: 0.85rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .pill-user-name {
        font-weight: 600;
        font-size: 0.9rem;
        color: var(--text-main, #0e1e16);
      }
      .pill-chevron {
        color: var(--text-muted, #72847b);
      }

      .account-dropdown-menu {
        position: absolute;
        top: calc(100% + 8px);
        right: 1rem;
        left: auto;
        min-width: 240px;
        max-width: min(320px, calc(100vw - 1.5rem));
        background: var(--bg-surface, #ffffff);
        border: 1px solid var(--border-subtle, #e2ece5);
        border-radius: 14px;
        box-shadow: 0 12px 30px -4px rgba(14, 30, 22, 0.1);
        padding: 0.5rem 0;
        z-index: 200;
        direction: rtl;
        animation: menuPop 0.15s ease-out;
      }
      @keyframes menuPop {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }

      .menu-user-header {
        padding: 0.6rem 1.15rem;
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
      }
      .menu-user-header strong {
        font-size: 0.92rem;
        color: var(--text-main, #0e1e16);
      }
      .menu-email {
        font-size: 0.78rem;
        color: var(--text-muted, #72847b);
        overflow-wrap: anywhere;
      }
      .menu-divider {
        height: 1px;
        background: var(--border-subtle, #e2ece5);
        margin: 0.35rem 0;
      }
      .menu-item {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 0.65rem;
        padding: 0.6rem 1.15rem;
        border: none;
        background: none;
        font-size: 0.88rem;
        font-weight: 500;
        color: var(--text-secondary, #485950);
        cursor: pointer;
        text-decoration: none;
        box-sizing: border-box;
        text-align: right;
        transition: background-color 0.12s ease;
      }
      .menu-item:hover {
        background: var(--bg-surface-soft, #f0f6f2);
        color: var(--text-main, #0e1e16);
      }
      .menu-item--danger {
        color: var(--price-expensive, #dc2626);
      }
      .menu-item--danger:hover {
        background: var(--price-expensive-bg, #fef2f2);
        color: #b91c1c;
      }

      .top-nav {
        display: flex;
        gap: 0.4rem;
        flex-wrap: wrap;
        direction: rtl;
        justify-content: flex-start;
      }

      .top-nav a {
        padding: 0.45rem 0.95rem;
        border-radius: 9999px;
        text-decoration: none;
        color: var(--text-secondary, #485950);
        font-size: 0.92rem;
        font-weight: 500;
        transition: all 0.15s ease;
      }

      .top-nav a:hover {
        background: var(--bg-surface-soft, #f0f6f2);
        color: var(--text-main, #0e1e16);
      }

      .top-nav a.active {
        background: var(--emerald-100, #d1fae5);
        color: var(--emerald-800, #065f46);
        font-weight: 600;
      }

      .admin-nav-item {
        position: relative;
        display: inline-flex;
        align-items: center;
      }

      .admin-menu-trigger {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.45rem 0.95rem;
        border-radius: 9999px;
        border: none;
        background: transparent;
        color: var(--text-secondary, #485950);
        font-size: 0.92rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s ease;
      }

      .admin-menu-trigger:hover {
        background: var(--bg-surface-soft, #f0f6f2);
        color: var(--text-main, #0e1e16);
      }

      .admin-menu-trigger.active {
        background: var(--emerald-100, #d1fae5);
        color: var(--emerald-800, #065f46);
        font-weight: 600;
      }

      .admin-dropdown-menu {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        min-width: 220px;
        background: var(--bg-surface, #ffffff);
        border: 1px solid var(--border-subtle, #e2ece5);
        border-radius: 14px;
        box-shadow: 0 12px 30px -4px rgba(14, 30, 22, 0.12);
        padding: 0.5rem 0;
        z-index: 200;
        animation: menuPop 0.15s ease-out;
        display: flex;
        flex-direction: column;
        direction: rtl;
      }

      .admin-menu-section-title {
        padding: 0.45rem 1rem 0.2rem;
        font-size: 0.72rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--text-muted, #72847b);
      }

      .admin-dropdown-menu a {
        display: block;
        padding: 0.55rem 1rem;
        text-decoration: none;
        color: var(--text-secondary, #485950);
        font-size: 0.88rem;
        font-weight: 500;
        transition: background-color 0.12s ease;
        border-radius: 0 !important;
      }

      .admin-dropdown-menu a:hover {
        background: var(--bg-surface-soft, #f0f6f2);
        color: var(--text-main, #0e1e16);
      }

      .admin-dropdown-menu a.active {
        background: var(--emerald-50, #ecfdf5);
        color: var(--emerald-800, #065f46);
        font-weight: 600;
      }

      .app-main {
        min-height: 0;
      }

      @media (max-width: 860px) {
        .app-header {
          flex-direction: column;
          align-items: stretch;
          padding: max(0.75rem, env(safe-area-inset-top)) 1.5rem 0.65rem;
          gap: 0;
          direction: ltr;
        }

        .header-mobile-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .header-bar {
          width: 100%;
          min-height: 42px;
          direction: ltr;
        }

        .brand-logo {
          height: 32px;
          max-width: 140px;
        }

        .header-desktop-account {
          display: none;
        }

        .header-action-btn,
        .header-location-btn,
        .hamburger-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          padding: 0;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #ffffff;
          color: #334155;
          cursor: pointer;
          transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .header-account-btn {
          overflow: hidden;
          color: #1f6435;
        }
        .header-avatar-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .header-avatar-fallback {
          font-weight: 700;
          font-size: 1rem;
        }

        .header-location-btn {
          color: #1f6435;
        }
        .header-location-btn:hover {
          background: #f1f8f3;
        }

        .hamburger-btn.is-active {
          background: #eff6ff;
          border-color: #bfdbfe;
          color: #1d4ed8;
        }

        .top-nav {
          display: none;
          flex-direction: column;
          gap: 0.25rem;
          width: 100%;
          padding-top: 0.75rem;
          margin-top: 0.5rem;
          border-top: 1px solid #f0f0f0;
        }

        .top-nav.open {
          display: flex;
        }

        .top-nav a {
          padding: 0.65rem 0.85rem;
        }

        .admin-nav-item {
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        .admin-menu-trigger {
          width: 100%;
          justify-content: space-between;
          padding: 0.65rem 0.85rem;
        }

        .admin-dropdown-menu {
          position: static;
          box-shadow: none;
          border: none;
          border-right: 2px solid var(--emerald-300, #6ee7b7);
          margin-inline-start: 0.75rem;
          padding: 0;
          background: transparent;
        }

        .account-dropdown-menu {
          position: fixed;
          top: 60px;
          right: 12px;
          left: 12px;
          max-width: 320px;
          margin: 0 auto;
        }
      }
    `
  ]
})
export class AppComponent implements AfterViewInit, OnDestroy {
  readonly auth = inject(AuthService);
  private readonly localProfile = inject(LocalProfileService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  private readonly diagnostics = inject(ClientDiagnosticsService);
  private readonly visitLog = inject(VisitLogService);
  private headerResize: ResizeObserver | null = null;

  isMenuOpen = false;
  isAccountMenuOpen = false;
  isAdminMenuOpen = false;
  activeModal: 'login' | 'profile' | 'groups' | null = null;

  get isAdminMenuActive(): boolean {
    const url = this.router.url;
    return (
      url.startsWith('/legacy-compare') ||
      url.startsWith('/similar') ||
      url.startsWith('/price-history') ||
      url.startsWith('/admin/')
    );
  }

  toggleAdminMenu(event?: MouseEvent): void {
    event?.stopPropagation();
    this.isAdminMenuOpen = !this.isAdminMenuOpen;
    if (this.isAdminMenuOpen) {
      this.isAccountMenuOpen = false;
    }
  }

  closeAdminMenu(): void {
    this.isAdminMenuOpen = false;
  }

  get userDisplayName(): string {
    const user = this.auth.currentUser;
    if (!user) return '';
    return user.givenName || (user.email ? user.email.split('@')[0] : '') || '';
  }

  constructor() {
    this.visitLog.start();
    this.diagnostics.start();

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.closeMenu();
        this.closeAccountMenu();
        this.closeAdminMenu();
        this.diagnostics.info('route', 'navigated');
        this.visitLog.noteNavigation(event.urlAfterRedirects);
      });

    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => this.diagnostics.error('navigation', event.error));

    this.auth.activeModal$.subscribe((modal) => {
      this.activeModal = modal;
    });
  }

  ngAfterViewInit(): void {
    const root = this.elementRef.nativeElement as HTMLElement;
    const header = root.querySelector('.app-header') as HTMLElement | null;
    const shell = root.querySelector('.shell') as HTMLElement | null;
    if (!header || !shell) return;

    const applyHeight = () => {
      shell.style.setProperty('--app-header-height', `${header.offsetHeight}px`);
    };
    applyHeight();
    if (typeof ResizeObserver !== 'undefined') {
      this.headerResize = new ResizeObserver(applyHeight);
      this.headerResize.observe(header);
    }
  }

  ngOnDestroy(): void {
    this.headerResize?.disconnect();
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    if (this.isMenuOpen) {
      this.isAccountMenuOpen = false;
    }
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  onMobileAccountClick(): void {
    if (!this.auth.isLoggedIn) {
      this.closeMenu();
      this.closeAccountMenu();
      this.openModal('login');
    } else {
      this.toggleAccountMenu();
    }
  }

  toggleAccountMenu(): void {
    this.isAccountMenuOpen = !this.isAccountMenuOpen;
    if (this.isAccountMenuOpen) {
      this.isMenuOpen = false;
    }
  }

  closeAccountMenu(): void {
    this.isAccountMenuOpen = false;
  }

  openModal(type: 'login' | 'profile' | 'groups'): void {
    this.activeModal = type;
    if (type === 'login') this.auth.openLoginModal();
    else if (type === 'profile') this.auth.openProfileModal();
    else if (type === 'groups') this.auth.openGroupsModal();
  }

  onModalTypeChange(type: 'login' | 'profile' | 'groups' | null): void {
    this.activeModal = type;
    if (type == null) {
      this.auth.closeModal();
    }
  }

  logout(): void {
    void this.auth.logout();
  }

  openLocationSettings(): void {
    this.closeMenu();
    this.closeAccountMenu();
    this.localProfile.requestOpenLocationSettings();
  }

  onNavClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a')) {
      this.closeMenu();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (this.isAccountMenuOpen) {
      const isInsideAccountMenu = target.closest('.account-dropdown-menu');
      const isAccountTrigger = target.closest('.header-account-btn') || target.closest('.desktop-user-pill');
      if (!isInsideAccountMenu && !isAccountTrigger) {
        this.closeAccountMenu();
      }
    }

    if (this.isAdminMenuOpen) {
      const isInsideAdmin = target.closest('.admin-nav-item');
      if (!isInsideAdmin) {
        this.closeAdminMenu();
      }
    }

    if (this.isMenuOpen) {
      const isInsideNav = target.closest('.top-nav');
      const isHamburger = target.closest('.hamburger-btn');
      if (!isInsideNav && !isHamburger) {
        this.closeMenu();
      }
    }
  }
}
